import React from 'react';
import { Link } from 'react-router-dom';
import Logo from '../assets/Logo_no_bg.png';
import { useMarket } from '../context/MarketContext';
import { getProductsForMarket } from '../data/products';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const { market } = useMarket();

  const footerLinks = {
    company: [
      { name: 'About', path: '/' },
      { name: 'Products', path: '/products' },
      { name: 'Services', path: '/services' },
      { name: 'Docs', path: '/docs' },
      { name: 'Pricing', path: '/pricing' },
      { name: 'Contact', path: '/contact' },
    ],
    products: getProductsForMarket(market).map((product) => ({
      name: product.name,
      path: `/products/${product.id}`,
    })),
    legal: [
      { name: 'Privacy Policy', path: '/privacy' },
      { name: 'Terms of Service', path: '/terms' },
    ],
  };

  return (
    <footer className="bg-slate-50 dark:bg-dark-950 border-t border-slate-200 dark:border-dark-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <img 
                src={Logo} 
                alt="Cimmple Logo" 
                className="h-10 w-auto dark:brightness-0 dark:invert"
              />
            </Link>
            <p className="text-slate-600 dark:text-dark-400 mb-6">
              Building modern software solutions that simplify your business operations.
            </p>
            <div className="flex gap-4">
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-dark-800 flex items-center justify-center text-slate-600 dark:text-dark-400 hover:text-primary-600 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-dark-700 transition-all duration-300"
                aria-label="Twitter"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-dark-800 flex items-center justify-center text-slate-600 dark:text-dark-400 hover:text-primary-600 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-dark-700 transition-all duration-300"
                aria-label="LinkedIn"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
              <a
                href="https://github.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-dark-800 flex items-center justify-center text-slate-600 dark:text-dark-400 hover:text-primary-600 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-dark-700 transition-all duration-300"
                aria-label="GitHub"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="text-slate-900 dark:text-white font-semibold mb-6">Company</h3>
            <ul className="space-y-4">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-slate-600 dark:text-dark-400 hover:text-primary-600 dark:hover:text-white transition-colors duration-300"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Products Links */}
          <div>
            <h3 className="text-slate-900 dark:text-white font-semibold mb-6">Products</h3>
            <ul className="space-y-4">
              {footerLinks.products.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-slate-600 dark:text-dark-400 hover:text-primary-600 dark:hover:text-white transition-colors duration-300"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
            
            {(market === 'US' || market === 'IN') && (
              <>
                <h3 className="text-slate-900 dark:text-white font-semibold mt-8 mb-4">Login</h3>
                <ul className="space-y-4">
                  <li>
                    <a
                      href="https://cimmple.net"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-600 dark:text-dark-400 hover:text-primary-600 dark:hover:text-white transition-colors duration-300"
                    >
                      CimmpleFlow
                    </a>
                  </li>
                  {market === 'US' && (
                    <li>
                      <a
                        href="https://payroll.cimmple.net"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-600 dark:text-dark-400 hover:text-primary-600 dark:hover:text-white transition-colors duration-300"
                      >
                        CimmplePay
                      </a>
                    </li>
                  )}
                </ul>
              </>
            )}
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-slate-900 dark:text-white font-semibold mb-6">Contact</h3>
            <ul className="space-y-4 text-slate-600 dark:text-dark-400">
              <li className="flex items-start gap-3">
                <svg className="w-5 h-5 mt-1 shrink-0 text-primary-600 dark:text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <div className="space-y-2 text-sm leading-snug">
                  <p>
                    <span className="font-medium text-slate-800 dark:text-dark-200">Cimmplex Innovations LLP</span>
                    <span className="block text-slate-500 dark:text-dark-500">India · Software development</span>
                  </p>
                  <p>
                    <span className="font-medium text-slate-800 dark:text-dark-200">Formonyx LLC</span>
                    <span className="block text-slate-500 dark:text-dark-500">United States · US address</span>
                  </p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <svg className="w-5 h-5 text-primary-600 dark:text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <a href="mailto:contact@cimmple.com" className="hover:text-primary-600 dark:hover:text-white transition-colors">
                  contact@cimmple.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-slate-200 dark:border-dark-800 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 dark:text-dark-500 text-sm">
            © {currentYear} Cimmplex Innovations LLP. All rights reserved.
          </p>
          <div className="flex gap-6">
            {footerLinks.legal.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className="text-slate-500 dark:text-dark-500 text-sm hover:text-primary-600 dark:hover:text-white transition-colors duration-300"
              >
                {link.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
